// @ts-nocheck

import {
  Box,
  Button,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Switch,
  Fade,
} from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import NodeTheme from '../resources/NodeTheme';
import HeaderBar, { CurrentPages } from '../header-bar/HeaderBar';
import { Fragment, useEffect, useState } from 'react';
import GradientBackground from '../resources/components/GradientBackground';
import PageHeader from '../components/PageHeader';

import DoneIcon from '@mui/icons-material/Done';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import useDashboardStateSynced from '../collaboration/useDashboardStateSynced';
import { useNavigate } from 'react-router-dom';
import useActiveOrganisation from '../collaboration/useActiveOrganisation';
import useActiveUser from '../collaboration/useActiveUser';
import { useTranslation } from 'react-i18next';

// Add type definition
type BankStatementFrequency = 'none' | 'daily' | 'monthly';

const OrganisationSettingsPage = (props) => {
  const { t } = useTranslation();

  const [activeOrganisation, setActiveOrganisation] = useActiveOrganisation();
  const { activeUser } = useActiveUser();
  const navigate = useNavigate();

  const [organisationMembers, setOrganisationMembers] = useState([]);
  const [apiKey, setApiKey] = useState('');
  const [accountantEmail, setAccountantEmail] = useState('');
  const [bankStatementFrequency, setBankStatementFrequency] =
    useState<BankStatementFrequency>('none');
  const [sendAllVouchers, setSendAllVouchers] = useState(false);
  const [accountingStartDate, setAccountingStartDate] = useState<Date | null>(
    null
  );
  const [accountingStartDateInput, setAccountingStartDateInput] = useState('');
  const [netvisorUserId, setNetvisorUserId] = useState('');
  const [netvisorUserKey, setNetvisorUserKey] = useState('');
  const [netvisorOrganizationId, setNetvisorOrganizationId] = useState('');
  const [fivaldiPartnerId, setFivaldiPartnerId] = useState('');
  const [fivaldiPartnerSecret, setFivaldiPartnerSecret] = useState('');
  const [fivaldiCustomerId, setFivaldiCustomerId] = useState('');
  const [fivaldiCompanyId, setFivaldiCompanyId] = useState('');
  const [
    fivaldiBookkeepingEntryInstructions,
    setFivaldiBookkeepingEntryInstructions,
  ] = useState('');
  const [
    fivaldiBookkeepingEntryParameters,
    setFivaldiBookkeepingEntryParameters,
  ] = useState('');
  const [
    fivaldiDefaultBookkeepingEntryInstructions,
    setFivaldiDefaultBookkeepingEntryInstructions,
  ] = useState('');
  const [
    fivaldiDefaultBookkeepingEntryParameters,
    setFivaldiDefaultBookkeepingEntryParameters,
  ] = useState('');
  const [
    originalFivaldiBookkeepingEntryInstructions,
    setOriginalFivaldiBookkeepingEntryInstructions,
  ] = useState('');
  const [
    originalFivaldiBookkeepingEntryParameters,
    setOriginalFivaldiBookkeepingEntryParameters,
  ] = useState('');
  const [fennoaUsername, setFennoaUsername] = useState('');
  const [fennoaApiKey, setFennoaApiKey] = useState('');
  const [isFortnoxEnabled, setIsFortnoxEnabled] = useState(false);
  const [isLoadingFortnox, setIsLoadingFortnox] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const [initialLoadDone, setInitialLoadDone] = useState(false);

  useEffect(() => {
    const fetchOrganisationMembers = async () => {
      const response = await fetch(
        `/api/organizations/${activeOrganisation.organizationId}/members`
      );
      const data = await response.json();

      data.sort((a, b) => b.createdAt - a.createdAt);
      setOrganisationMembers(data);
    };
    fetchOrganisationMembers();
  }, [setOrganisationMembers]);

  useEffect(() => {
    const fetchApiKey = async () => {
      const response = await fetch(
        `/api/organizations/${activeOrganisation.organizationId}/apiKey`
      );
      const data = await response.json();

      setApiKey(data.apiKey);
    };
    fetchApiKey();
  }, [setApiKey]);

  // Check if Fortnox is enabled
  useEffect(() => {
    const checkFortnoxEnabled = async () => {
      if (!activeOrganisation?.organizationId) return;

      try {
        setIsLoadingFortnox(true);
        const response = await fetch('/api/fortnox/enabled', {
          headers: {
            'x-organization': activeOrganisation.organizationId,
          },
        });

        if (response.ok) {
          const isEnabled = await response.json();
          setIsFortnoxEnabled(isEnabled);
        }
      } catch (error) {
        console.error('Error checking Fortnox status:', error);
      } finally {
        setIsLoadingFortnox(false);
      }
    };

    checkFortnoxEnabled();
  }, [activeOrganisation?.organizationId]);

  const [newUserId, setNewUserId] = useState('');

  const addUser = async () => {
    setNewUserId('');

    const id = newUserId.trim();

    if (id.length == 0) {
      alert('empty user id');
      return;
    }

    setNewUserId('');

    const response = await fetch(
      `/api/organizations/${activeOrganisation.organizationId}/userRights/${id}`,
      {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ rights: 'member' }),
      }
    );

    const member = await response.json();

    setOrganisationMembers([...organisationMembers, member]);
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      if (!activeOrganisation?.organizationId) return;

      try {
        setIsLoading(true);

        const headers = {
          'x-organization': activeOrganisation.organizationId,
        };

        const [
          preferencesResponse,
          emailResponse,
          netvisorResponse,
          fivaldiResponse,
          fennoaResponse,
        ] = await Promise.all([
          fetch(`/api/accounting/accountant/preferences`, { headers }),
          fetch(`/api/accounting/accountant/email`, { headers }),
          fetch(`/api/accounting/integrations/netvisor`, { headers }),
          fetch(`/api/accounting/integrations/fivaldi`, { headers }),
          fetch(`/api/accounting/integrations/fennoa`, { headers }),
        ]);

        if (preferencesResponse.ok) {
          const { preferences } = (await preferencesResponse.json()) || {
            preferences: {},
          };
          setBankStatementFrequency(
            preferences.bankStatementFrequency || 'none'
          );
          setSendAllVouchers(preferences.sendAllVouchers || false);
          if (preferences.accountingStartDate) {
            setAccountingStartDate(preferences.accountingStartDate);
            setAccountingStartDateInput(preferences.accountingStartDate);
          }
        }

        if (emailResponse.ok) {
          const { email } = (await emailResponse.json()) || {};
          setAccountantEmail(email);
        }

        if (netvisorResponse.ok) {
          const netvisorData = await netvisorResponse.json();
          setNetvisorUserId(netvisorData.userId || '');
          setNetvisorUserKey(netvisorData.userKey || '');
          setNetvisorOrganizationId(netvisorData.organizationId || '');
        }

        if (fivaldiResponse.ok) {
          const fivaldiData = await fivaldiResponse.json();
          setFivaldiPartnerId(fivaldiData.partnerId || '');
          setFivaldiPartnerSecret(fivaldiData.partnerSecret || '');
          setFivaldiCustomerId(fivaldiData.customerId || '');
          setFivaldiCompanyId(fivaldiData.companyId || '');
          setFivaldiBookkeepingEntryInstructions(
            fivaldiData.bookkeepingEntryInstructions || ''
          );
          setFivaldiBookkeepingEntryParameters(
            fivaldiData.bookkeepingEntryParameters || ''
          );
          setOriginalFivaldiBookkeepingEntryInstructions(
            fivaldiData.bookkeepingEntryInstructions || ''
          );
          setOriginalFivaldiBookkeepingEntryParameters(
            fivaldiData.bookkeepingEntryParameters || ''
          );
          setFivaldiDefaultBookkeepingEntryInstructions(
            fivaldiData.defaultBookkeepingEntryInstructions || ''
          );
          setFivaldiDefaultBookkeepingEntryParameters(
            fivaldiData.defaultBookkeepingEntryParameters || ''
          );
        }

        if (fennoaResponse.ok) {
          const fennoaData = await fennoaResponse.json();
          setFennoaUsername(fennoaData.username || '');
          setFennoaApiKey(fennoaData.apiKey || '');
        }
        setTimeout(() => {
          setInitialLoadDone(true);
        });
      } catch (error) {
        console.error('Error fetching initial data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInitialData();
  }, [activeOrganisation?.organizationId]);

  const saveAccountantEmail = async (email: string) => {
    try {
      await fetch(`/api/accounting/accountant/email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-organization': activeOrganisation.organizationId,
        },
        body: JSON.stringify({ email }),
      });
    } catch (error) {
      console.error('Error saving accountant email:', error);
    }
  };

  const saveAccountantPreferences = async () => {
    try {
      await fetch(`/api/accounting/accountant/preferences`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-organization': activeOrganisation.organizationId,
        },
        body: JSON.stringify({
          preferences: {
            bankStatementFrequency,
            sendAllVouchers,
            accountingStartDate,
          },
        }),
      });
    } catch (error) {
      console.error('Error saving accountant preferences:', error);
    }
  };

  useEffect(() => {
    if (initialLoadDone) saveAccountantPreferences();
  }, [
    initialLoadDone,
    JSON.stringify({
      bankStatementFrequency,
      sendAllVouchers,
      accountingStartDate,
    }),
  ]);

  const saveNetvisorCredentials = async () => {
    try {
      await fetch(`/api/accounting/integrations/netvisor`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-organization': activeOrganisation.organizationId,
        },
        body: JSON.stringify({
          userId: netvisorUserId,
          userKey: netvisorUserKey,
          organizationId: netvisorOrganizationId,
        }),
      });
    } catch (error) {
      console.error('Error saving Netvisor credentials:', error);
    }
  };

  const saveFivaldiCredentials = async () => {
    try {
      const requestBody: any = {
        partnerId: fivaldiPartnerId,
        partnerSecret: fivaldiPartnerSecret,
        customerId: fivaldiCustomerId,
        companyId: fivaldiCompanyId,
      };

      if (fivaldiBookkeepingEntryInstructions.trim()) {
        // Validate length before saving
        if (fivaldiBookkeepingEntryInstructions.length > 1024) {
          alert(
            'Bookkeeping Entry Instructions cannot be longer than 1024 characters.'
          );
          return;
        }

        // Save if it's different from the default value, OR if it's the same as default but we had a custom value before
        if (
          fivaldiBookkeepingEntryInstructions !==
            fivaldiDefaultBookkeepingEntryInstructions ||
          (fivaldiBookkeepingEntryInstructions ===
            fivaldiDefaultBookkeepingEntryInstructions &&
            originalFivaldiBookkeepingEntryInstructions !== '')
        ) {
          requestBody.bookkeepingEntryInstructions =
            fivaldiBookkeepingEntryInstructions;
        }
      }

      if (fivaldiBookkeepingEntryParameters.trim()) {
        // Save if it's different from the default value, OR if it's the same as default but we had a custom value before
        if (
          fivaldiBookkeepingEntryParameters !==
            fivaldiDefaultBookkeepingEntryParameters ||
          (fivaldiBookkeepingEntryParameters ===
            fivaldiDefaultBookkeepingEntryParameters &&
            originalFivaldiBookkeepingEntryParameters !== '')
        ) {
          // Validate JSON before saving
          try {
            JSON.parse(fivaldiBookkeepingEntryParameters);
            requestBody.bookkeepingEntryParameters =
              fivaldiBookkeepingEntryParameters;
          } catch (error) {
            alert(
              'Invalid JSON format in Bookkeeping Entry Parameters. Please check your JSON syntax.'
            );
            return;
          }
        }
      }

      await fetch(`/api/accounting/integrations/fivaldi`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-organization': activeOrganisation.organizationId,
        },
        body: JSON.stringify(requestBody),
      });
    } catch (error) {
      console.error('Error saving Fivaldi credentials:', error);
    }
  };

  const callUser = async (userId, task) => {
    await fetch(`/api/voip/call`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-organization': activeOrganisation.organizationId,
      },
      body: JSON.stringify({
        userId,
        task,
      }),
    });
  };

  const saveFennoaCredentials = async () => {
    try {
      await fetch(`/api/accounting/integrations/fennoa`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-organization': activeOrganisation.organizationId,
        },
        body: JSON.stringify({
          username: fennoaUsername,
          apiKey: fennoaApiKey,
        }),
      });
    } catch (error) {
      console.error('Error saving Fennoa credentials:', error);
    }
  };

  const startFortnoxAuthorization = async () => {
    try {
      const response = await fetch('/auth/fortnox', {
        headers: {
          'x-organization': activeOrganisation.organizationId,
        },
      });

      if (response.ok) {
        const url = await response.json();
        if (url) {
          window.location.href = url;
        }
      }
    } catch (error) {
      console.error('Error starting Fortnox authorization:', error);
    }
  };

  return (
    <ThemeProvider theme={NodeTheme}>
      <GradientBackground sx={{ minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
          <HeaderBar currentPage={CurrentPages.Settings} />
          <Fade in={!isLoading} timeout={500}>
            <Box
              sx={{
                flex: 1,
              }}
            >
              <Box
                sx={{
                  paddingTop: { xs: 2, sm: 3 },
                  paddingLeft: { xs: 2, sm: 3 },
                  paddingRight: { xs: 2, sm: 3 },
                  paddingBottom: 0,
                }}
              >
                <PageHeader
                  title={t('Organisation Settings')}
                  sx={{ marginBottom: 0 }}
                />
              </Box>
              <Container
                maxWidth="md"
                sx={{
                  padding: {
                    xs: 2,
                    sm: 3,
                  },
                  paddingTop: 0,
                }}
              >
                {/* Accountant Settings Section */}
                <Paper sx={{ p: 3, mb: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" gutterBottom>
                    {t('Accountant Settings')}
                  </Typography>

                  <TextField
                    fullWidth
                    label={t('Accountant email')}
                    value={accountantEmail}
                    onChange={async (e) => {
                      setAccountantEmail(e.target.value);
                      try {
                        await saveAccountantEmail(e.target.value);
                      } catch (error) {
                        console.error('Error saving accountant email:', error);
                      }
                    }}
                    sx={{ mb: 2 }}
                  />

                  <Typography variant="subtitle1" gutterBottom>
                    {t('Bank Statement Frequency')}
                  </Typography>

                  {/* Bank Statement Frequency Options */}
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Switch
                        checked={bankStatementFrequency === 'none'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBankStatementFrequency('none');
                          }
                        }}
                      />
                      <Typography>
                        {t('Do not send bank statements')}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Switch
                        checked={bankStatementFrequency === 'daily'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBankStatementFrequency('daily');
                          }
                        }}
                      />
                      <Typography>{t('Send bank statements daily')}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                      <Switch
                        checked={bankStatementFrequency === 'monthly'}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setBankStatementFrequency('monthly');
                          }
                        }}
                      />
                      <Typography>
                        {t('Send bank statements monthly')}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Switch
                      checked={sendAllVouchers}
                      onChange={(e) => {
                        setSendAllVouchers(e.target.checked);
                      }}
                    />
                    <Typography>{t('Send all vouchers')}</Typography>
                  </Box>

                  <TextField
                    type="date"
                    label={t('Accounting start date')}
                    value={accountingStartDateInput}
                    onChange={(e) => {
                      setAccountingStartDateInput(e.target.value);
                      const date = new Date(e.target.value);
                      if (!isNaN(date.getTime())) {
                        setAccountingStartDate(e.target.value);
                      }
                    }}
                    fullWidth
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Paper>

                {/* Netvisor Integration Section */}
                <Paper sx={{ p: 3, mb: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" gutterBottom>
                    {t('Netvisor Integration')}
                  </Typography>

                  <TextField
                    fullWidth
                    label={t('Organization ID')}
                    value={netvisorOrganizationId}
                    onChange={(e) => setNetvisorOrganizationId(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label={t('User ID')}
                    value={netvisorUserId}
                    onChange={(e) => setNetvisorUserId(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label={t('User key')}
                    type="password"
                    value={netvisorUserKey}
                    onChange={(e) => setNetvisorUserKey(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <Button variant="contained" onClick={saveNetvisorCredentials}>
                    {t('Save Netvisor Settings')}
                  </Button>
                </Paper>

                {/* Fivaldi Integration Section */}
                <Paper sx={{ p: 3, mb: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" gutterBottom>
                    {t('Fivaldi Integration')}
                  </Typography>

                  <TextField
                    fullWidth
                    label={t('Partner ID')}
                    value={fivaldiPartnerId}
                    onChange={(e) => setFivaldiPartnerId(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label={t('Partner Secret')}
                    type="password"
                    value={fivaldiPartnerSecret}
                    onChange={(e) => setFivaldiPartnerSecret(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label={t('Customer ID')}
                    value={fivaldiCustomerId}
                    onChange={(e) => setFivaldiCustomerId(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label={t('Company ID')}
                    value={fivaldiCompanyId}
                    onChange={(e) => setFivaldiCompanyId(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      label={t('Bookkeeping Entry Instructions')}
                      value={
                        fivaldiBookkeepingEntryInstructions ||
                        fivaldiDefaultBookkeepingEntryInstructions
                      }
                      onChange={(e) =>
                        setFivaldiBookkeepingEntryInstructions(e.target.value)
                      }
                      helperText={t(
                        'Instructions for bookkeeping entries in Fivaldi'
                      )}
                      placeholder={fivaldiDefaultBookkeepingEntryInstructions}
                      error={
                        (
                          fivaldiBookkeepingEntryInstructions ||
                          fivaldiDefaultBookkeepingEntryInstructions
                        ).length > 1024
                      }
                      inputProps={{
                        maxLength: 1024,
                      }}
                    />
                    {fivaldiBookkeepingEntryInstructions && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          setFivaldiBookkeepingEntryInstructions('')
                        }
                        sx={{ mt: 1 }}
                      >
                        {t('Revert to Default')}
                      </Button>
                    )}
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <TextField
                      fullWidth
                      multiline
                      rows={6}
                      label={t('Bookkeeping Entry Parameters')}
                      value={
                        fivaldiBookkeepingEntryParameters ||
                        fivaldiDefaultBookkeepingEntryParameters
                      }
                      onChange={(e) =>
                        setFivaldiBookkeepingEntryParameters(e.target.value)
                      }
                      helperText={t(
                        'JSON parameters for bookkeeping entries in Fivaldi. Must be valid JSON format.'
                      )}
                      placeholder={fivaldiDefaultBookkeepingEntryParameters}
                      error={
                        (
                          fivaldiBookkeepingEntryParameters ||
                          fivaldiDefaultBookkeepingEntryParameters
                        ).trim() !== '' &&
                        (() => {
                          try {
                            JSON.parse(
                              fivaldiBookkeepingEntryParameters ||
                                fivaldiDefaultBookkeepingEntryParameters
                            );
                            return false;
                          } catch {
                            return true;
                          }
                        })()
                      }
                    />
                    {fivaldiBookkeepingEntryParameters && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setFivaldiBookkeepingEntryParameters('')}
                        sx={{ mt: 1 }}
                      >
                        {t('Revert to Default')}
                      </Button>
                    )}
                  </Box>

                  <Button variant="contained" onClick={saveFivaldiCredentials}>
                    {t('Save Fivaldi Settings')}
                  </Button>
                </Paper>

                {/* Fennoa Integration Section */}
                <Paper sx={{ p: 3, mb: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" gutterBottom>
                    {t('Fennoa Integration')}
                  </Typography>

                  <TextField
                    fullWidth
                    label={t('Username')}
                    value={fennoaUsername}
                    onChange={(e) => setFennoaUsername(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <TextField
                    fullWidth
                    label={t('API key')}
                    type="password"
                    value={fennoaApiKey}
                    onChange={(e) => setFennoaApiKey(e.target.value)}
                    sx={{ mb: 2 }}
                  />

                  <Button variant="contained" onClick={saveFennoaCredentials}>
                    {t('Save Fennoa Settings')}
                  </Button>
                </Paper>

                {/* Fortnox Integration Section */}
                <Paper sx={{ p: 3, mb: { xs: 2, sm: 3 } }}>
                  <Typography variant="h6" gutterBottom>
                    {t('Fortnox Integration')}
                  </Typography>

                  {isLoadingFortnox ? (
                    <CircularProgress size={24} sx={{ my: 2 }} />
                  ) : isFortnoxEnabled ? (
                    <Box sx={{ my: 2 }}>
                      <Typography
                        color="success.main"
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <DoneIcon /> {t('Fortnox integration is enabled')}
                      </Typography>
                    </Box>
                  ) : (
                    <Box sx={{ my: 2 }}>
                      <Typography sx={{ mb: 2 }}>
                        {t(
                          'Connect your Fortnox account to enable integration'
                        )}
                      </Typography>
                      <Button
                        variant="contained"
                        onClick={startFortnoxAuthorization}
                      >
                        {t('Connect to Fortnox')}
                      </Button>
                    </Box>
                  )}
                </Paper>

                {/* Existing Organization Members Section */}
                <Paper sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('Organisation members')}
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('Name')}</TableCell>
                          <TableCell>{t('User identifier')}</TableCell>
                          <TableCell>{t('Rights')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {organisationMembers.map((member, index) => (
                          <TableRow key={index + 'frewfew'}>
                            <TableCell>{member.name}</TableCell>
                            <TableCell>{member.userId}</TableCell>
                            <TableCell>{member.rights}</TableCell>
                            <Button
                              onClick={() => {
                                const task = prompt('Sinin tehtävä puheluun');

                                callUser(member.userId, task);
                              }}
                            >
                              {t('Call')}
                            </Button>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('Add new member')}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                      <TextField
                        placeholder={t('User identifier')}
                        sx={{ flexGrow: 1 }}
                        value={newUserId}
                        onChange={(event) => setNewUserId(event.target.value)}
                        onKeyDown={async (event) => {
                          if (event.key === 'Enter') {
                            addUser();
                          }
                        }}
                      />
                      <Button variant="contained" onClick={() => addUser()}>
                        {t('Add')}
                      </Button>
                    </Box>
                  </Box>
                </Paper>
              </Container>
            </Box>
          </Fade>
        </Box>
      </GradientBackground>
    </ThemeProvider>
  );
};

export default OrganisationSettingsPage;
